import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FORM_MODE, Permissions } from '@awp/constants';
import { Row, Col, Button, FormLabel } from 'react-bootstrap';
import { getAvalancheGeographicAreas } from '@awp/stores/avalancheSlice';
import { FormSelect } from '@awp/components/Common/AwpFormInputs';
import { Formik, Form } from 'formik';
import Authorize from '@awp/components/Authorize';
import { ActionMenuView, ActionMenuEdit } from '@awp/components/Common/AwpActionMenus';
import Table from '@components/table';
import { sortByNumericThenAlpha } from '@awp/helpers/sorter';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { AvalancheAreasService } from '@components/AvalancheAreasAdmin/AvalancheAreasService';

// Filter function to simplify filtering logic
const filterAvalancheAreas = (areas, program, search, statusFilter) => {
  let filtered = program ? areas.filter((x) => x.parentId === program.value) : areas;

  if (!isValueEmpty(search)) {
    const searchUpper = search.toUpperCase();
    filtered = filtered.filter(
      (x) => x.name?.toUpperCase().includes(searchUpper) || x.code?.toUpperCase().includes(searchUpper),
    );
  }

  if (statusFilter) {
    const isActive = statusFilter === 'Active';
    filtered = filtered.filter((x) => (isActive ? !x.isDisabled : x.isDisabled));
  }

  return filtered.sort((a, b) => a.name.localeCompare(b.name));
};

// Action Menu Component
const ActionMenu = ({ row, setData, setMode, setSearch }) => (
  <div>
    <Authorize requires={[Permissions.WriteAdminTools]}>
      <ActionMenuEdit
        onClick={() => {
          setData(row.original);
          setMode(FORM_MODE.EDIT);
          setSearch('');
        }}
      />
    </Authorize>
    <ActionMenuView
      onClick={() => {
        setData(row.original);
        setMode(FORM_MODE.VIEW);
        setSearch('');
      }}
    />
  </div>
);

const AvalancheAreasTable = ({ isMedium, isLarge, setMode, mode, loading, setData, search, setSearch, areaList }) => {
  const programAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const avalanchePrograms = programAreas.filter((x) => !x.isDisabled);

  const [localAreaList, setLocalAreaList] = useState(areaList);
  const [program, setProgram] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);

  const crudService = new AvalancheAreasService(mode);

  useEffect(() => {
    setLocalAreaList(filterAvalancheAreas(areaList, program, search, statusFilter));
  }, [search, statusFilter, program, areaList]);

  const handleProgramChange = (opt) => {
    setProgram(opt);
    setLocalAreaList(filterAvalancheAreas(areaList, opt, search, statusFilter));
  };

  const columns = useMemo(
    () => [
      { Header: 'Avalanche Area Name', accessor: 'name', sortType: sortByNumericThenAlpha },
      { Header: 'Code', accessor: 'code', sortType: sortByNumericThenAlpha },
      {
        Header: 'Status',
        accessor: 'isDisabled',
        sortType: sortByNumericThenAlpha,
        Cell: ({ row }) => <div className="text-wrap">{row.original.isDisabled ? 'Inactive' : 'Active'}</div>,
      },
      {
        Header: 'Actions',
        accessor: 'Id',
        disableSortBy: true,
        Cell: (props) => <ActionMenu {...props} setData={setData} setMode={setMode} setSearch={setSearch} />,
      },
    ],
    [isLarge, isMedium],
  );

  return (
    <>
      <Formik initialValues={{ validateOnMount: true }}>
        {() => (
          <>
            <div className="d-flex">{crudService.crud.getHeader()}</div>
            <Form>
              <Row>
                <Col sm={5}>
                  <FormLabel>
                    <b>Search All</b>
                  </FormLabel>
                  <input
                    className="form-control"
                    placeholder="Search..."
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Col>
                <Col sm={5}>
                  <FormSelect
                    className="avalanche-select"
                    name="program"
                    label="Program"
                    isClearable
                    postChange={handleProgramChange}
                    options={avalanchePrograms}
                  />
                </Col>
                <Col sm={2} className="mb-4">
                  <FormSelect
                    className="status-select"
                    name="statusFilter"
                    label="Status"
                    isClearable
                    options={[
                      { value: 'Active', label: 'Active' },
                      { value: 'Inactive', label: 'Inactive' },
                    ]}
                    postChange={(e) => setStatusFilter(e?.value || null)}
                  />
                </Col>
              </Row>
            </Form>
          </>
        )}
      </Formik>

      {localAreaList.length > 0 ? (
        <Table columns={columns} data={localAreaList} fetchData={() => {}} loading={loading} defaultSortBy="name" />
      ) : (
        <div className="mt-4">
          <p>No records found</p>
        </div>
      )}

      <div style={{ paddingTop: '1.25rem' }}>
        <Authorize requires={[Permissions.WriteAdminTools]}>
          <Button
            variant="primary"
            onClick={() => {
              setMode(FORM_MODE.CREATE);
              setSearch('');
            }}
            disabled={loading}
          >
            Create
          </Button>
        </Authorize>
      </div>
    </>
  );
};

export default AvalancheAreasTable;
